import { Link } from "react-router-dom";
import {
  ArrowRight,
  BedDouble,
  Building2,
  Bus,
  Compass,
  Globe2,
  Landmark,
  Sparkles,
  Tent,
  Users,
} from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import PageStickyNav from "@/components/PageStickyNav";

// -----------------------------------------------------------------------------
//  TeamPage — /team
//
//  Editorial hub introducing the entire Grup Xaluca ecosystem: the people
//  AND the infrastructure that makes the Xaluca experience possible. The
//  page reads as a "quiet manifesto" — not a sterile "About us" org chart,
//  but a set of pillar-cards that map the moving parts of the group.
//
//  Structure
//  ─────────
//  • Hero — overline, headline, intro. Mirrors the layout language of
//    every other top-of-page block on the site.
//  • Pillars grid — one card per operational area (hotels, camps, events,
//    Spanish travel agency, Moroccan travel agency, transport, fleet,
//    operations, people). Icon + title + description.
//  • Manifesto strip — one short paragraph on the human values that
//    stitch the whole ecosystem together.
//  • Closing CTA — sends visitors to /contact.
//
//  Adding a new pillar = adding one entry to the AREAS array. Zero design
//  changes needed.
// -----------------------------------------------------------------------------

const pick = (obj, lang) => (obj?.[lang] || obj?.en || obj?.fr || obj?.es || "");

const COPY = {
  overline: { en: "THE ECOSYSTEM", fr: "L'ÉCOSYSTÈME", es: "EL ECOSISTEMA" },
  title: {
    en: "The people and the machinery of Grup Xaluca",
    fr: "Les femmes, les hommes et l'infrastructure de Grup Xaluca",
    es: "Las personas y la infraestructura de Grup Xaluca",
  },
  intro: {
    en: "Behind every welcome, every route, every dinner under the stars, there is a group of people and a set of moving parts. Hotels, desert camps, agencies on both sides of the Strait, vehicles and drivers, operations, and stories. This is the ecosystem that makes Xaluca possible.",
    fr: "Derrière chaque accueil, chaque route, chaque dîner sous les étoiles, il y a un groupe de personnes et une organisation en mouvement. Hôtels, campements du désert, agences des deux côtés du détroit, véhicules et chauffeurs, opérations, et histoires. C'est l'écosystème qui rend Xaluca possible.",
    es: "Detrás de cada bienvenida, de cada ruta y de cada cena bajo las estrellas, hay un grupo de personas y una maquinaria en movimiento. Hoteles, campamentos en el desierto, agencias a ambos lados del estrecho, vehículos y chóferes, operaciones y relatos. Este es el ecosistema que hace posible Xaluca.",
  },
  manifestoOverline: {
    en: "OUR WAY OF WORKING",
    fr: "NOTRE FAÇON DE TRAVAILLER",
    es: "NUESTRA FORMA DE TRABAJAR",
  },
  manifesto: {
    en: "We are neither a chain nor a franchise. We are a family group of southern Moroccan roots and a European gaze, built over decades one route, one welcome, one detail at a time. Every property, every driver, every operations lead is part of the same conversation — the conversation of hosting well.",
    fr: "Nous ne sommes ni une chaîne ni une franchise. Nous sommes un groupe familial aux racines du sud marocain et au regard européen, construit au fil des décennies, une route, un accueil, un détail à la fois. Chaque adresse, chaque chauffeur, chaque responsable des opérations fait partie de la même conversation — celle du soin de l'accueil.",
    es: "No somos una cadena ni una franquicia. Somos un grupo familiar de raíces del sur de Marruecos y mirada europea, construido con décadas de trabajo, ruta a ruta, bienvenida a bienvenida, detalle a detalle. Cada alojamiento, cada chófer, cada responsable de operaciones forma parte de la misma conversación — la del cuidado en el acoger.",
  },
  ctaOverline: {
    en: "GET IN TOUCH",
    fr: "CONTACTEZ-NOUS",
    es: "HABLEMOS",
  },
  ctaTitle: {
    en: "Ready to write your own chapter?",
    fr: "Prêt·e à écrire votre propre chapitre ?",
    es: "¿Listos para escribir vuestro propio capítulo?",
  },
  ctaButton: {
    en: "Talk to our team",
    fr: "Parlez à notre équipe",
    es: "Habla con nuestro equipo",
  },
};

const AREAS = [
  {
    id: "hotels",
    icon: BedDouble,
    stat: "8",
    name: { en: "Hotels", fr: "Hôtels", es: "Hoteles" },
    description: {
      en: "From the palm groves of Arfoud to the Atlas foothills, our hotels are the quiet homes of every Xaluca journey — each one shaped by its landscape.",
      fr: "Des palmeraies d'Arfoud aux contreforts de l'Atlas, nos hôtels sont les maisons discrètes de chaque voyage Xaluca — chacun modelé par son paysage.",
      es: "Desde los palmerales de Arfoud hasta las estribaciones del Atlas, nuestros hoteles son las casas silenciosas de cada viaje Xaluca — cada uno modelado por su paisaje.",
    },
  },
  {
    id: "desert-camps",
    icon: Tent,
    stat: "3",
    name: {
      en: "Desert camps",
      fr: "Campements du désert",
      es: "Campamentos del desierto",
    },
    description: {
      en: "Three signature bivouacs deep in the Erg Chebbi dunes, from raw nomad camps to tented suites — the pillow-menu-in-the-Sahara level of comfort.",
      fr: "Trois bivouacs signature au cœur des dunes de l'Erg Chebbi, du campement nomade brut aux suites sous tente — le niveau du menu-d'oreillers-dans-le-Sahara.",
      es: "Tres bivouacs con firma propia en las dunas del Erg Chebbi, desde campamentos nómadas más crudos hasta suites entoldadas — el nivel del menú-de-almohadas-en-el-Sahara.",
    },
  },
  {
    id: "desert-events",
    icon: Sparkles,
    name: {
      en: "Desert event logistics",
      fr: "Logistique événementielle du désert",
      es: "Logística de eventos en el desierto",
    },
    description: {
      en: "Weddings, brand launches, corporate retreats — our operations team choreographs full production in the dunes, from generators to gala tables.",
      fr: "Mariages, lancements de marque, séminaires — notre équipe orchestre la production complète dans les dunes, des générateurs aux tables de gala.",
      es: "Bodas, lanzamientos de marca, retiros corporativos — nuestro equipo de operaciones orquesta la producción completa en las dunas, desde los generadores hasta las mesas de gala.",
    },
  },
  {
    id: "agency-spain",
    icon: Globe2,
    name: {
      en: "Travel agency · Spain",
      fr: "Agence de voyage · Espagne",
      es: "Agencia de viajes · España",
    },
    description: {
      en: "Xaluca Tours, our Spain-based agency specialised in Morocco. The bridge between European travellers and the south of the country.",
      fr: "Xaluca Tours, notre agence basée en Espagne spécialisée dans le Maroc. Le pont entre les voyageurs européens et le sud du pays.",
      es: "Xaluca Tours, nuestra agencia con sede en España especializada en Marruecos. El puente entre los viajeros europeos y el sur del país.",
    },
  },
  {
    id: "agency-morocco",
    icon: Landmark,
    name: {
      en: "Travel agency · Morocco",
      fr: "Agence de voyage · Maroc",
      es: "Agencia de viajes · Marruecos",
    },
    description: {
      en: "Our Moroccan DMC — locally licensed, locally led — designs tailored routes from Marrakech to the Sahara, with the eye and access only insiders have.",
      fr: "Notre DMC marocaine — sous licence locale, direction locale — conçoit des itinéraires sur mesure de Marrakech au Sahara, avec le regard et l'accès des initiés.",
      es: "Nuestra DMC marroquí — con licencia local y dirección local — diseña rutas a medida de Marrakech al Sáhara, con la mirada y el acceso que solo se tiene desde dentro.",
    },
  },
  {
    id: "transport",
    icon: Bus,
    name: {
      en: "Transport company",
      fr: "Société de transport",
      es: "Empresa de transportes",
    },
    description: {
      en: "Licensed transport for groups and small parties across Morocco. Own dispatch, own drivers, own safety standards — no third parties, no surprises.",
      fr: "Transport agréé pour groupes et petits comités partout au Maroc. Notre propre régulation, nos chauffeurs, nos standards de sécurité — aucun sous-traitant, aucune surprise.",
      es: "Transporte con licencia para grupos y comitivas pequeñas por todo Marruecos. Central propia, chóferes propios, estándares de seguridad propios — sin subcontratas, sin sorpresas.",
    },
  },
  {
    id: "fleet",
    icon: Compass,
    stat: "40+",
    name: {
      en: "Vehicle fleet",
      fr: "Flotte de véhicules",
      es: "Flota de vehículos",
    },
    description: {
      en: "4x4s for the piste, mini-vans for cultural circuits, executive sedans for airport pickups — a fleet sized and maintained for the terrain, not just the road.",
      fr: "4x4 pour la piste, mini-vans pour les circuits culturels, berlines exécutives pour les transferts aéroport — une flotte dimensionnée et entretenue pour le terrain, pas seulement la route.",
      es: "4x4 para la pista, mini-vans para circuitos culturales, berlinas ejecutivas para traslados de aeropuerto — una flota dimensionada y mantenida para el terreno, no solo para la carretera.",
    },
  },
  {
    id: "operations",
    icon: Building2,
    name: {
      en: "Operations & services",
      fr: "Opérations & services",
      es: "Operaciones y servicios",
    },
    description: {
      en: "Central bookings, hospitality training, procurement, and 24/7 in-country support — the invisible layer that keeps every stay running smoothly.",
      fr: "Réservations centralisées, formation à l'hospitalité, achats et support 24/7 sur place — la couche invisible qui fait tourner chaque séjour sans accroc.",
      es: "Reservas centralizadas, formación en hospitalidad, compras y soporte 24/7 en destino — la capa invisible que hace que cada estancia funcione con fluidez.",
    },
  },
  {
    id: "people",
    icon: Users,
    stat: "200+",
    name: {
      en: "The people",
      fr: "Les personnes",
      es: "Las personas",
    },
    description: {
      en: "Waiters, camel guides, concierges, chefs, drivers, receptionists, camp masters — over two hundred souls from the south of Morocco whose craft is hosting.",
      fr: "Serveurs, chameliers, concierges, chefs, chauffeurs, réceptionnistes, chefs de camp — plus de deux cents âmes du sud du Maroc dont le métier est d'accueillir.",
      es: "Camareros, cameleros, conserjes, chefs, chóferes, recepcionistas, jefes de campamento — más de doscientas personas del sur de Marruecos cuyo oficio es acoger.",
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────
//  Pillar card
// ─────────────────────────────────────────────────────────────────────────
const PillarCard = ({ area, lang }) => {
  const Icon = area.icon;
  const name = pick(area.name, lang);
  const description = pick(area.description, lang);
  return (
    <article
      className="group relative p-8 md:p-10 bg-white border border-[#E8DFCF] shadow-[0_4px_20px_-12px_rgba(44,38,33,0.15)] hover:shadow-[0_18px_40px_-20px_rgba(44,38,33,0.35)] transition-shadow"
      data-testid={`team-pillar-${area.id}`}
    >
      {/* Icon + optional stat */}
      <div className="flex items-start justify-between gap-4">
        <div className="w-12 h-12 rounded-full bg-[#F5EDDF] border border-[#E8DFCF] flex items-center justify-center text-[#C16542] group-hover:bg-[#C16542] group-hover:text-white transition-colors">
          <Icon className="w-5 h-5" strokeWidth={1.6} />
        </div>
        {area.stat ? (
          <div className="text-right">
            <div className="font-serif-x text-3xl md:text-4xl text-[#2C2621] leading-none">
              {area.stat}
            </div>
          </div>
        ) : null}
      </div>

      <h3
        className="mt-6 font-serif-x text-2xl md:text-3xl leading-tight text-[#2C2621]"
        data-testid={`team-pillar-name-${area.id}`}
      >
        {name}
      </h3>
      <p className="mt-3 text-base leading-relaxed text-[#5C5248]">
        {description}
      </p>
    </article>
  );
};

// ─────────────────────────────────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────────────────────────────────
const TeamPage = () => {
  const { lang } = useLanguage();
  return (
    <div
      className="bg-[#FDFBF7] text-[#2C2621] pt-20 md:pt-24 pb-24 md:pb-32"
      data-testid="page-team"
    >
      <PageStickyNav
        topOffset={64}
        items={[
          { id: "team-pillars", label: { en: "The team", fr: "L'équipe", es: "El equipo" } },
          { id: "team-manifesto", label: { en: "Manifesto", fr: "Manifeste", es: "Manifiesto" } },
          { id: "team-cta", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Editorial header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24">
          <div className="md:col-span-6">
            <p className="overline text-[#C16542]">{pick(COPY.overline, lang)}</p>
            <h1 className="font-serif-x mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {pick(COPY.title, lang)}
            </h1>
          </div>
          <p className="md:col-span-5 md:col-start-8 self-end text-base md:text-lg text-[#5C5248] leading-relaxed">
            {pick(COPY.intro, lang)}
          </p>
        </div>

        {/* Pillar grid */}
        <div
          id="team-pillars"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 scroll-mt-32"
          data-testid="team-pillars"
        >
          {AREAS.map((area) => (
            <PillarCard key={area.id} area={area} lang={lang} />
          ))}
        </div>
      </div>

      {/* Manifesto strip — dark break in the light-page flow, ties the pillars
          back to the human tone of /founders. */}
      <section
        id="team-manifesto"
        className="relative isolate mt-24 md:mt-32 bg-[#1A1513] text-[#FDFBF7] py-24 md:py-32 overflow-hidden scroll-mt-32"
        data-testid="team-manifesto"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 50% 30%, rgba(193,101,66,0.10) 0%, rgba(26,21,19,0) 60%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="overline text-[#C16542]">
            {pick(COPY.manifestoOverline, lang)}
          </p>
          <p className="mt-8 font-serif-x text-2xl md:text-3xl lg:text-4xl leading-[1.4] text-[#FDFBF7]">
            {pick(COPY.manifesto, lang)}
          </p>
        </div>
      </section>

      {/* Closing CTA */}
      <section id="team-cta" className="max-w-4xl mx-auto px-6 md:px-12 mt-24 md:mt-32 text-center scroll-mt-32">
        <p className="overline text-[#C16542]">{pick(COPY.ctaOverline, lang)}</p>
        <h2 className="font-serif-x mt-4 text-3xl md:text-4xl lg:text-5xl text-[#2C2621] leading-tight">
          {pick(COPY.ctaTitle, lang)}
        </h2>
        <div className="mt-8 flex justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#2C2621] text-[#FDFBF7] px-6 py-3.5 text-xs tracking-[0.3em] uppercase hover:bg-[#C16542] transition-colors"
            data-testid="team-cta-contact"
          >
            {pick(COPY.ctaButton, lang)}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
